import { supabase } from "@/integrations/supabase/client";
import { isLiveListing, sampleListings, type PropertyListing } from "@/data/sample-listings";
import { normalizePriceType, parsePriceValue, type PriceType } from "@/lib/pricing";

const PUBLISHED_SITE_URL = "https://www.vista360.ng";
const DEFAULT_PLACEHOLDER_IMAGE = "https://images.unsplash.com/photo-1519167758481-83f550bb49b3?w=800&q=80";

/** Map a DB row to the frontend PropertyListing shape */
function rowToListing(r: any): PropertyListing {
  const fallbackPriceTypeByCategory: Record<string, PriceType> = {
    "event-centre": "event",
    hotel: "hotel",
    restaurant: "event",
    apartment: "sale",
    office: "sale",
  };

  const normalizedPricing = normalizePriceType(
    r.price_type,
    fallbackPriceTypeByCategory[r.category] ?? "sale",
  );
  const parsedPrice = parsePriceValue(r.price);

  return {
    id: r.id,
    slug: r.slug,
    title: r.title,
    description: r.description ?? "",
    category: r.category,
    city: r.city,
    location: r.location,
    price: parsedPrice.amount,
    priceText: parsedPrice.priceText,
    priceType: normalizedPricing.priceType,
    rentFrequency: normalizedPricing.rentFrequency,
    imageUrl: r.image_url || DEFAULT_PLACEHOLDER_IMAGE,
    tourUrl: r.tour_url ?? "",
    videoUrl: r.video_url ?? undefined,
    videoPosterUrl: r.video_poster_url ?? undefined,
    amenities: r.amenities ?? [],
    rating: Number(r.rating) || 0,
    reviewCount: r.review_count ?? 0,
    featured: r.featured ?? false,
    capacity: r.capacity ?? undefined,
    size: r.size ?? undefined,
    coordinates: r.coordinates ?? undefined,
    beaconNumber: r.beacon_number ?? undefined,
    verified: r.verified ?? false,
    captureDate: r.capture_date ?? undefined,
    architectFirm: r.architect_firm ?? undefined,
    galleryImages: r.gallery_images ?? undefined,
    galleryPreviewImages: r.gallery_preview_images ?? undefined,
    isLive: r.is_live ?? undefined,
  };
}

/**
 * Fetch approved listings from the database.
 * Falls back to sample data if the query fails.
 */
export async function fetchApprovedListings(): Promise<PropertyListing[]> {
  try {
    const { data, error } = await supabase
      .from("listings")
      .select("*")
      .eq("status", "approved")
      .order("featured", { ascending: false })
      .order("created_at", { ascending: false });

    if (error || !data || data.length === 0) {
      console.warn("Falling back to sample listings:", error?.message);
      return sampleListings.filter(isLiveListing);
    }

    const gatedRows = data.filter((row: any) => {
      // Backward compatible: if payment_status is absent, keep pre-gating behavior.
      if (typeof row.payment_status !== "string") return true;
      return row.payment_status === "paid" || row.payment_status === "waived";
    });

    return gatedRows.map(rowToListing);
  } catch {
    return sampleListings.filter(isLiveListing);
  }
}

export async function fetchListingById(id: string): Promise<PropertyListing | null> {
  try {
    const { data } = await supabase.from("listings").select("*").eq("id", id).maybeSingle();
    if (data) return rowToListing(data);
  } catch { /* fallback */ }
  return sampleListings.find((l) => l.id === id) ?? null;
}

export async function fetchListingBySlug(slug: string): Promise<PropertyListing | null> {
  try {
    const { data } = await supabase.from("listings").select("*").eq("slug", slug).maybeSingle();
    // Use Supabase data only if it has a valid tour URL
    if (data && data.tour_url) {
      const listing = rowToListing(data);
      // If Supabase data is missing media fields, merge with sample data
      const sampleData = sampleListings.find((l) => l.slug === slug);
      if (sampleData) {
        return {
          ...listing,
          videoUrl: listing.videoUrl ?? sampleData.videoUrl,
          videoPosterUrl: listing.videoPosterUrl ?? sampleData.videoPosterUrl,
          galleryImages: listing.galleryImages ?? sampleData.galleryImages,
          galleryPreviewImages: listing.galleryPreviewImages ?? sampleData.galleryPreviewImages,
        };
      }
      return listing;
    }
  } catch { /* fallback */ }
  return sampleListings.find((l) => l.slug === slug) ?? null;
}

export function getTourShareUrl(slug: string): string {
  // Use the server-rendered /share/tour/ endpoint so WhatsApp, Telegram,
  // and other crawlers receive tour-specific OG meta tags (image, title).
  // The endpoint auto-redirects browsers to the real SPA tour page.
  return `${PUBLISHED_SITE_URL}/share/tour/${slug}`;
}

export async function incrementWhatsAppClick(listingId: string) {
  try {
    await supabase.from("tour_stats").insert({
      listing_id: listingId,
      event_type: "whatsapp_click",
    });
  } catch { /* non-critical */ }
}

export async function incrementTourView(listingId: string) {
  try {
    await supabase.from("tour_stats").insert({
      listing_id: listingId,
      event_type: "tour_view",
    });
  } catch { /* non-critical */ }
}
